import { useState } from "react";
import { AiBlock } from "@/components/ax/AiBlock";
import { Button } from "@/components/ax/Button";
import { EPISTEMIC, EpistemicRow } from "@/components/ax/EpistemicRow";
import { Field } from "@/components/ax/Field";
import { RadioChips } from "@/components/ax/RadioChips";
import { Sheet } from "@/components/ax/Sheet";
import { useI18n } from "@/i18n";

const EditSheet = ({ suggestion, onClose, onSave, busy }) => {
  const { t } = useI18n();
  const epistemicOptions = EPISTEMIC.map((v) => ({ value: v, label: t(`epistemic.${v}`), mono: true }));
  const [text, setText] = useState(suggestion.text);
  const [epistemic, setEpistemic] = useState("");
  const [error, setError] = useState();
  const save = () => {
    if (text.trim().length < 3) return setError(t("err.item"));
    onSave(suggestion, text, epistemic);
  };
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} title={t("items.editTitle")} testId="edit-item-sheet">
      <Field
        multiline
        label={t("intake.step.items")}
        maxLength={280}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setError();
        }}
        error={error}
        testId="edit-item-text"
      />
      <RadioChips
        className="mt-4"
        legend={t("items.status")}
        name="edit-status"
        testId="edit-item-status"
        value={epistemic}
        onChange={setEpistemic}
        options={epistemicOptions}
      />
      <Button
        variant="primary"
        disabled={!epistemic || busy}
        busy={busy}
        onClick={save}
        data-testid="edit-item-save"
        className="mt-6 w-full"
      >
        {t("items.save")}
      </Button>
      <Button onClick={onClose} data-testid="edit-item-close" className="mt-2 w-full">
        {t("items.close")}
      </Button>
    </Sheet>
  );
};

export const StepItems = ({ s }) => {
  const { t } = useI18n();
  const epistemicOptions = EPISTEMIC.map((v) => ({ value: v, label: t(`epistemic.${v}`), mono: true }));
  const [editing, setEditing] = useState(null);
  const busy = s.busy === "item" || s.busy === "status";
  return (
    <div data-testid="intake-step-3" className="enter mt-8">
      {s.suggestions.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {s.suggestions.map((x) => (
            <li key={x.id}>
              <AiBlock testId={`suggestion-${x.id}`}>
                <p className="m-0 text-base leading-6 text-ax-text [overflow-wrap:anywhere]">{x.text}</p>
                <span className="mt-2 inline-flex h-6 items-center rounded-[8px] border border-ax-border px-2 font-mono text-[13px] leading-[18px] text-ax-muted">
                  {t("items.suggested")}
                </span>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button disabled={busy} onClick={() => s.acceptSuggestion(x)} data-testid={`suggestion-accept-${x.id}`} className="min-h-[44px]">
                    {t("items.accept")}
                  </Button>
                  <Button disabled={busy} onClick={() => setEditing(x)} data-testid={`suggestion-edit-${x.id}`} className="min-h-[44px]">
                    {t("items.edit")}
                  </Button>
                  <Button disabled={busy} onClick={() => s.dropSuggestion(x.id)} data-testid={`suggestion-discard-${x.id}`} className="min-h-[44px]">
                    {t("items.discard")}
                  </Button>
                </div>
              </AiBlock>
            </li>
          ))}
        </ul>
      )}

      {s.items.length > 0 && (
        <ul data-testid="item-list" className={`m-0 flex list-none flex-col gap-3 p-0 ${s.suggestions.length ? "mt-6" : ""}`}>
          {s.items.map((it) => (
            <EpistemicRow key={it.id} item={it} onChange={s.changeStatus} busy={busy} />
          ))}
        </ul>
      )}

      <div className="mt-8 flex flex-col gap-4">
        <Field
          multiline
          label={t("items.addLabel")}
          maxLength={280}
          value={s.itemDraft.text}
          onChange={(e) => {
            s.setItemDraft((d) => ({ ...d, text: e.target.value }));
            s.setErrors((x) => ({ ...x, item: undefined }));
          }}
          error={s.errors.item}
          testId="item-add-text"
        />
        <RadioChips
          legend={t("items.status")}
          name="add-status"
          testId="item-add-status"
          value={s.itemDraft.epistemic}
          onChange={(v) => s.setItemDraft((d) => ({ ...d, epistemic: v }))}
          options={epistemicOptions}
        />
        <Button
          disabled={!s.itemDraft.text || !s.itemDraft.epistemic || busy}
          busy={s.busy === "item"}
          onClick={s.addManual}
          data-testid="item-add-button"
          className="self-start"
        >
          {t("items.add")}
        </Button>
      </div>
      {!s.hasKA && (
        <p data-testid="items-hint" className="m-0 mt-4 text-sm leading-5 text-ax-muted">
          {t("items.hint")}
        </p>
      )}
      {editing && (
        <EditSheet
          suggestion={editing}
          busy={busy}
          onClose={() => setEditing(null)}
          onSave={async (sg, text, ep) => {
            await s.editSuggestion(sg, text, ep);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};
